import React, { SyntheticEvent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Typography, Button, Box, Card, Divider, Grid, TextField, FormControl, InputLabel, Select, MenuItem, CardContent, CardActions, } from '@mui/material';
import { NavLink, Link } from 'react-router-dom';
import Title from '../../../components/Title';
import { Type_Operation } from '../../../models/type_operation';
import { Type_Expense } from '../../../models/type_expense';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const TipoGastoAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = "Administrar Tipo Gastos";
  const navigate = useNavigate();

  // Estados
  const [name, setName] = useState('');
  const [type_expense_id, setType_expense_id] = useState('');
  const [type_operation_id, setType_operation_id] = useState('');
  const [operations, setOperations] = useState<Type_Operation[]>([]);
  const [typeexpense, setTypeExpense] = useState<Type_Expense[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);

  const selectedTypeExpense = typeexpense.find(
    te => te.id.toString() === type_expense_id,
  );
  const isCobroOrOficina =
    (selectedTypeExpense?.name || '').toLowerCase().includes('cobro') ||
    (selectedTypeExpense?.name || '').toLowerCase().includes('oficina');
  const RESTA_OPERATION_ID = '2';

  const operationsFiltered = isCobroOrOficina
    ? operations.filter(op => op.id.toString() === RESTA_OPERATION_ID)
    : operations;

  const safeOperationsFiltered =
    operationsFiltered.length > 0 ? operationsFiltered : operations;

  useEffect(() => {
    // Si el gasto es de cobro u oficina, forzamos a que la operación sea "resta".
    if (isCobroOrOficina) {
      setType_operation_id(RESTA_OPERATION_ID);
    } else {
      setType_operation_id('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCobroOrOficina]);

  // Manejar el envío del formulario
  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!name || !type_expense_id || !type_operation_id) {
      showAlert('Tiene campos pendientes por diligenciar!', 'warning');
      return;
    }

    const formData = {
      name,
      type_expense_id,
      type_operation_id,
    };

    try {
      showLoading();
      await axios.post('/expenses', formData);
      showAlert('Se ha realizado el registro exitosamente!', 'success');
      setTimeout(() => navigate('/administrar/tipogasto'), 1000);
    } catch (error) {
      showAlert('Error al registrar el gasto. Por favor, intente nuevamente.', 'error');
    } finally {
      hideLoading();
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const { data: operationData } = await axios.get('typeoperation');
        setOperations(operationData.data);

        const { data: expenseData } = await axios.get('typeexpense');
        setTypeExpense(expenseData.data);

      } catch (error) {
        showAlert('Error al cargar los datos. Por favor, intente nuevamente.', 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, []);

  // Cargar gastos basados en el tipo de operación seleccionado
  useEffect(() => {
    if (type_operation_id) {
      const fetchExpenses = async () => {
        try {
          const { data } = await axios.get(`expenseoperation/${type_operation_id}`);
          setExpenses(data.data);
        } catch (error) {
          showAlert('Error al cargar los gastos. Por favor, intente nuevamente.', 'error');
        }
      };

      fetchExpenses();
    } else {
      setExpenses([]);
    }
  }, [type_operation_id]);

  return (
    <HeaderResponsive>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Title>Crear Información Tipo Gastos</Title>
      </Box>

      <Card>
        <Divider sx={{ margin: 0 }} />
        <form onSubmit={handleSubmit}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Descripción"
                  size="small"
                  placeholder="Descripción"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  inputProps={{ maxLength: 30 }}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Seleccionar Tipo de Gasto</InputLabel>
                  <Select
                    value={type_expense_id}
                    size="small"
                    onChange={(e) => setType_expense_id(e.target.value as string)}
                    label="Seleccionar Tipo de Gasto"
                    required
                  >
                    {typeexpense.map((te) => (
                      <MenuItem key={te.id} value={te.id.toString()}>
                        {te.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="operation">Seleccionar Tipo Operación</InputLabel>
                  <Select
                    label="Operación"
                    size="small"
                    value={type_operation_id}
                    onChange={(e) => setType_operation_id(e.target.value)}
                    labelId="operation"
                    required
                    disabled={isCobroOrOficina}
                  >
                    {safeOperationsFiltered.map((op) => (
                      <MenuItem key={op.id} value={op.id.toString()}>
                        {op.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>

          <Divider sx={{ margin: 0 }} />
          <CardActions>
            <Button size='small' type="submit" sx={{ mr: 2 }} variant="contained">
              Guardar
            </Button>
            <Link to="/administrar/tipogasto">
              <Button size='small' color="secondary" variant="outlined">
                Cancelar
              </Button>
            </Link>
          </CardActions>
        </form>
      </Card>
    </HeaderResponsive>
  );
};

export default TipoGastoAdd;